/**
 * Application Logs API.
 *
 * Callback style `(args…, successCb, errorCb)` to match every other service here — a
 * promise-returning service would be the only one in the codebase.
 *
 * Two things differ from the other services, both because this one polls:
 *   - every read accepts a CancelToken, so a slow scan started on the previous file cannot land
 *     after the user has moved on and overwrite the newer view;
 *   - isCancel is exported, because a cancelled request is not an error and must not raise a toast.
 */
import axios from 'axios'
import { API } from './url'
import { downloadFile } from '@/shared/blobDownload'

function errMsg(e, fallback) {
  return (e.response && e.response.data && e.response.data.message) || fallback
}

/** 429 is expected under load, not a fault. Give it its own wording. */
function readError(e, fallback) {
  if (e.response && e.response.status === 429) {
    return 'The server is busy reading other logs. Try again in a moment.'
  }
  return errMsg(e, fallback)
}

export default {
  isCancel: function(e) { return axios.isCancel(e) },
  newCancelSource: function() { return axios.CancelToken.source() },

  getSources: function(success, error) {
    axios.get(API.logs.sources)
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Failed to load log sources')) })
  },

  getAllSources: function(success, error) {
    axios.get(API.logs.allSources)
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Failed to load log sources')) })
  },

  getFiles: function(code, params, success, error, cancelToken) {
    axios.get(API.logs.files(code), { params: params, cancelToken: cancelToken })
      .then(function(r) { success(r.data) })
      .catch(function(e) { if (!axios.isCancel(e)) error(errMsg(e, 'Failed to list log files')) })
  },

  tail: function(code, params, success, error, cancelToken) {
    axios.get(API.logs.tail(code), { params: params, cancelToken: cancelToken })
      .then(function(r) { success(r.data) })
      .catch(function(e) {
        if (axios.isCancel(e)) { return }
        // 415 is the binary guard; the body still describes the file, so hand it to the caller.
        if (e.response && e.response.status === 415) { success(e.response.data); return }
        error(readError(e, 'Failed to read log file'))
      })
  },

  content: function(code, params, success, error, cancelToken) {
    axios.get(API.logs.content(code), { params: params, cancelToken: cancelToken })
      .then(function(r) { success(r.data) })
      .catch(function(e) {
        if (axios.isCancel(e)) { return }
        if (e.response && e.response.status === 415) { success(e.response.data); return }
        error(readError(e, 'Failed to read log file'))
      })
  },

  search: function(code, params, success, error, cancelToken) {
    axios.get(API.logs.search(code), { params: params, cancelToken: cancelToken })
      .then(function(r) { success(r.data) })
      .catch(function(e) {
        if (axios.isCancel(e)) { return }
        if (e.response && e.response.status === 415) { success(e.response.data); return }
        error(readError(e, 'Search failed'))
      })
  },

  download: function(code, params, filename, success, error) {
    downloadFile(API.logs.download(code), params, filename, success, error)
  },

  // ── configuration ──────────────────────────────────────────────────────────

  validateSource: function(body, success, error) {
    axios.post(API.logs.validate, body)
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Could not validate that directory')) })
  },

  createSource: function(body, success, error) {
    axios.post(API.logs.sources, body)
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Failed to create log source')) })
  },

  updateSource: function(id, body, success, error) {
    axios.put(API.logs.source(id), body)
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Failed to update log source')) })
  },

  deleteSource: function(id, success, error) {
    axios.delete(API.logs.source(id))
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Failed to delete log source')) })
  },

  getAudit: function(params, success, error) {
    axios.get(API.logs.audit, { params: params })
      .then(function(r) { success(r.data) })
      .catch(function(e) { error(errMsg(e, 'Failed to load the access trail')) })
  }
}
